// src/oura/merchant/Reports.tsx
//
// Reports: every report we sent, newest first, then the dates of the next
// ones and the AI check. In the redesign the page is Documents: the reports
// and every doc shared with the merchant, in one list they can search, filter
// and sort (DocumentLibrary.tsx). A row opens the report drawer, where the report is
// read in the platform's PDF viewer or downloaded.
//
// The AI check is monthly and every monthly report carries it (YW, 28 Sep
// 2026), so the page says so once and no row repeats it; the next check is
// the next report's date.
//
// Approved documents (Docs phase 2, B24) are rows of the same list: when a
// named person files the PDF of a version the client approved, the server
// adds a report of kind "doc_approved" ("Google profile, Bukit Bintang,
// approved v1.0"), opened by the same drawer. One path, so a PDF is never
// listed or counted twice.
//
// "Coming next" reads the published schedule and never works out a date
// from today: the server says which month of the term it is, and a month's
// report counts as sent by the rule Home's dates use (`reportSent`).
//
// `?report=<id>` opens that report's drawer on arrival (Your plan links its
// execution timeline this way), then comes off the URL.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarIcon, FileCheck2Icon, FileTextIcon, EyeIcon } from "lucide-react";
import { readViewAs } from "@util/viewAs";
import { isInAppSnapshot, markDocumentRead, type ClientPagePayload, type ClientReportLink, type ClientStats, type MerchantScheduleMonth } from "@service/aeo/clientReview";
import {
    ArrowLink,
    Bento,
    BentoStack,
    CardEmpty,
    Drawer,
    DrawerBlock,
    DrawerHead,
    FoldCard,
    FoldScope,
    IconSquare,
    INLINE_PILL,
    InnerStat,
    ListRow,
    PageHead,
    Span2,
    Steps,
    Tag,
    TYPE,
} from "@oura/ui";
import { cn } from "../lib/cn";
import { dayDate } from "../lib/oura";
import { CLIENT_HINT, OURA_ROOT } from "../nav";
import { clientArea, CLIENT_PAGE_SENTENCE, MERCHANT_V2 } from "../merchantV2";
import { useMerchantDrawers } from "./useMerchantDrawers";
import { ReportMeta } from "./drawers/ReportDrawer";
import { DocumentLibrary } from "./DocumentLibrary";
import { PlanDrawer } from "./drawers/PlanDrawer";
import { aiBandLine, openableReports } from "./reportWording";
import { plural } from "./asks";
import { comingUp, parseDay, upcomingReports } from "./model";
import { ClientMonthlyReport } from "../components/search/MonthlyReport";

/**
 * "November Monthly Report": named for the month it arrives in, title case as a name. "Monthly",
 * not "baseline": the baseline report is the one-off Baseline Snapshot sent
 * with the execution timeline; these are the month-by-month reports.
 */
function reportName(m: MerchantScheduleMonth): string {
    const d = parseDay(m.reportDate);
    return d ? `${d.toLocaleString("en-GB", { month: "long" })} Monthly Report` : "Your next Monthly Report";
}

/** The next 2 reports: this month of the term onwards, minus any month whose report has gone out. */
function comingReports(payload: ClientPagePayload): MerchantScheduleMonth[] {
    return upcomingReports(payload).slice(0, 2);
}

/** Newest first; a report with no readable date goes last. */
function byNewest(a: ClientReportLink, b: ClientReportLink): number {
    const x = parseDay(a.date)?.getTime() ?? null;
    const y = parseDay(b.date)?.getTime() ?? null;
    if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
    return y - x;
}

export function Reports({ payload }: { payload: ClientPagePayload }) {
    const drawers = useMerchantDrawers();
    const [month, setMonth] = useState<MerchantScheduleMonth | null>(null);
    /**
     * Which reports were unread WHEN THIS PAGE OPENED (#4026 C-FE-2).
     *
     * A report is marked read when it is OPENED (the drawer marks it,
     * MerchantDrawers' openReport), not when the page is visited: the list
     * now pages and filters on "Not opened yet", so a visit that never showed
     * a report must not clear it (Jack's review of #4656). The first load's
     * answer is kept for the visit, less what is opened during it.
     *
     * A report with nothing to open is never "New"; the Baseline Snapshot,
     * drawn in the app with no file, is.
     */
    const newOnArrival = useRef<Set<string> | null>(null);
    if (newOnArrival.current === null) {
        newOnArrival.current = new Set(
            payload.reports.filter((r) => r.unread === true && (Boolean(r.url) || isInAppSnapshot(r))).map((r) => r.id),
        );
    }
    const wasNew = newOnArrival.current;
    // Opened on this visit, by the client: its dot goes at once.
    const [openedNow, setOpenedNow] = useState<ReadonlySet<string>>(() => new Set());
    const reportIsNew = useCallback((id: string) => wasNew.has(id) && !openedNow.has(id), [wasNew, openedNow]);
    const openReportRow = useCallback(
        (id: string) => {
            drawers.openReport(id);
            // A staffer's preview leaves the merchant's marks alone, as the drawer does.
            if (payload.previewedByStaff || readViewAs().active) return;
            setOpenedNow((was) => new Set(was).add(id));
        },
        [drawers, payload.previewedByStaff],
    );

    // Shared docs opened on this visit: the dot goes at once, before the next
    // read of the page carries the server's own answer (#6108). The client's
    // own open only: a staffer's preview leaves the dot for them.
    const [readNow, setReadNow] = useState<ReadonlySet<string>>(() => new Set());
    const viewDoc = useCallback(
        (id: string) => {
            if (payload.previewedByStaff || readViewAs().active) return;
            void markDocumentRead(id).then((marked) => {
                if (marked) setReadNow((was) => new Set(was).add(id));
            });
        },
        [payload.previewedByStaff],
    );

    // `?report=<id>` (Your plan's "Open in Reports"): open that report's drawer once, then take it off the URL,
    // so a refresh or Back does not open it again. An id not in the list opens nothing (openReport checks).
    const [searchParams, setSearchParams] = useSearchParams();
    const reportParam = searchParams.get("report");
    const { openReport } = drawers;
    useEffect(() => {
        if (!reportParam) return;
        openReport(reportParam);
        setSearchParams(
            (params) => {
                params.delete("report");
                return params;
            },
            { replace: true },
        );
    }, [reportParam, openReport, setSearchParams]);
    const coming = useMemo(() => comingReports(payload), [payload]);
    // Coming next's dates are the published schedule, the one Your plan lays
    // out month by month: the card opens the same drawer Home's "Your plan" does.
    const [planOpen, setPlanOpen] = useState(0);
    const planDates = useMemo(() => comingUp(payload), [payload]);
    const reports = useMemo(() => [...payload.reports].sort(byNewest), [payload.reports]);
    const schedule = payload.schedule;
    const next = coming.length > 0 ? coming[0] : null;
    // The date Home gives (its Coming up list), so the two screens never name different days.
    const homeNext = comingUp(payload).find((d) => d.kind === "report")?.date ?? null;
    const nextOn = dayDate(homeNext ?? next?.reportDate);

    // Only the reports they can open. A row that is "Not uploaded yet" stays in
    // the list, greyed, but is not one they have (#4031).
    const openable = openableReports(reports);
    const sentence = [
        openable.length > 0 ? `${plural(openable.length, "report")} so far.` : "No reports yet.",
        nextOn && `The next comes ${nextOn}.`,
        // Said once for the page, never per row: every monthly report carries it.
        (schedule || payload.engagement) && "Every monthly report includes the AI check.",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <FoldScope value="merchant-reports">
            {/* No "next report" line in the head: it is a date, and dates
                belong on the card that holds them (#4062 MR-FE-3). */}
            <PageHead
                over={payload.business.name}
                title={MERCHANT_V2 ? "Documents" : "Reports"}
                sentence={MERCHANT_V2 ? CLIENT_PAGE_SENTENCE.reports : CLIENT_HINT.reports}
                status={MERCHANT_V2 ? undefined : sentence}
            />
            <div className="mb-5 empty:hidden">
                <ClientMonthlyReport key={payload.business.id} businessId={payload.business.id} />
            </div>
            <Bento layout="c3">
                <Span2>
                    {MERCHANT_V2 ? (
                        <DocumentLibrary
                            payload={payload}
                            reports={reports}
                            documents={drawers.docs.documents}
                            documentsRefreshFailed={drawers.docs.refreshFailed}
                            reportIsNew={reportIsNew}
                            readNow={readNow}
                            onOpenReport={openReportRow}
                            onViewDoc={viewDoc}
                        />
                    ) : (
                        <FoldCard
                            id="reports"
                            title="Your reports"
                            icon={{ icon: FileTextIcon, area: clientArea("setup") }}
                            right={openable.length > 0 ? String(openable.length) : undefined}
                        >
                            {reports.length === 0 ? (
                                <p className={cn(TYPE.tb, "m-0 text-gray-500")}>None yet.</p>
                            ) : (
                                <div>
                                    {reports.map((r) => (
                                        <ReportRow key={r.id} report={r} isNew={wasNew.has(r.id)} onOpen={() => drawers.openReport(r.id)} />
                                    ))}
                                </div>
                            )}
                        </FoldCard>
                    )}
                </Span2>
                {/* Two cards in the short column, so neither ends in empty white beside the list. */}
                <BentoStack>
                    <FoldCard
                        id="coming"
                        title="Coming next"
                        icon={{ icon: CalendarIcon, area: clientArea("dates") }}
                        right={coming.length > 0 ? plural(coming.length, "report") : undefined}
                    >
                        {coming.length > 0 ? (
                            <Steps
                                items={coming.map((m) => ({
                                    key: String(m.termMonth),
                                    title: reportName(m),
                                    meta: dayDate(m.reportDate),
                                    state: "next",
                                    onClick: () => setMonth(m),
                                }))}
                            />
                        ) : null}
                        {MERCHANT_V2 && schedule && (
                            <ArrowLink onClick={() => setPlanOpen((n) => n + 1)} className="mt-3">
                                Your plan
                            </ArrowLink>
                        )}
                        {coming.length > 0 ? null : !MERCHANT_V2 ? (
                            <p className={cn(TYPE.tb, "m-0 text-gray-500")}>
                                {schedule ? "No more reports this term." : "Your report dates appear here once your plan is published."}
                            </p>
                        ) : schedule ? (
                            <CardEmpty icon={CalendarIcon} title="No more reports this term">
                                Your next term&rsquo;s report dates appear here once they are set.
                            </CardEmpty>
                        ) : (
                            <CardEmpty icon={CalendarIcon} title="No report dates yet">
                                Your report dates appear here once your plan is published.
                            </CardEmpty>
                        )}
                    </FoldCard>
                    <AiCheckCard aiCheck={payload.stats.aiCheck} next={next} scheduled={Boolean(schedule)} started={Boolean(payload.subscription?.startedAt)} />
                </BentoStack>
            </Bento>
            <ComingDrawer month={month} onClose={() => setMonth(null)} />
            {MERCHANT_V2 && (
                <PlanDrawer
                    key={planOpen}
                    open={planOpen > 0}
                    onClose={() => setPlanOpen(0)}
                    payload={payload}
                    dates={planDates}
                    openKey={null}
                    onAnswer={(askId) => {
                        setPlanOpen(0);
                        drawers.openQuestion(askId);
                    }}
                />
            )}
        </FoldScope>
    );
}

function ReportRow({ report, isNew, onOpen }: { report: ClientReportLink; isNew: boolean; onOpen: () => void }) {
    const file = Boolean(report.url);
    const approved = report.kind === "doc_approved";
    return (
        <ListRow
            // Red for a PDF they can open, green for a document they approved.
            // A report with no file yet is greyed and has no Open; its row
            // still opens what we know about it.
            // One colour for a report, because every row here IS one: red
            // against green said "PDF" against "approved document", which is
            // our distinction and not the client's (#4062 MR-FE-8). No file
            // stays grey -- that is a state they can act on, or not.
            icon={<IconSquare tone={!file ? "gray" : MERCHANT_V2 ? "blue" : approved ? "green" : "red"} icon={approved ? FileCheck2Icon : FileTextIcon} />}
            title={report.title}
            sub={<ReportMeta report={report} />}
            // The existing Tag, no new colour: "New" is a state, not a
            // severity, and the list already uses tone for what a row IS.
            tags={isNew ? <Tag tone="blue">New</Tag> : undefined}
            muted={!file}
            right={file ? <span className={cn(INLINE_PILL.base, INLINE_PILL.secondary, INLINE_PILL.row)}>{MERCHANT_V2 ? "Read" : "Open"}</span> : undefined}
            onClick={onOpen}
            className="py-3"
        />
    );
}

/**
 * The AI check beside the reports. It comes with every monthly report, so the
 * next one is the next report's date (the one Home gives), and the last one
 * shows when there was one. The check is read on Visibility, which the card
 * opens.
 */
function AiCheckCard({ aiCheck, next, scheduled, started }: { aiCheck: ClientStats["aiCheck"]; next: MerchantScheduleMonth | null; scheduled: boolean; started: boolean }) {
    const on = dayDate(next?.reportDate);
    const last = dayDate(aiCheck.measuredAt);
    return (
        <FoldCard id="ai-check" title="AI check" icon={{ icon: EyeIcon, area: clientArea("ai") }} foldedFact={on ? `Next ${on}` : undefined}>
            <InnerStat
                label="Next check"
                // A plan that has started but has no published dates is waiting on the publish, not on the start (#4379).
                value={on ?? (scheduled ? "None left this term" : started ? "Dated once your plan is published" : "Dated when your plan starts")}
                // The band is the finding whether or not the check carries a
                // date, and a missing one says why (staging f73b9933).
                sub={[
                    next && `With your ${reportName(next)}.`,
                    last && `Last checked ${last}.`,
                    aiCheck.band
                        ? `${aiBandLine(aiCheck.band)}.`
                        : aiCheck.bandAbsent === "no_assessment" && "Your first scan has not run yet.",
                ].filter(Boolean).join(" ") || undefined}
            />
            <ArrowLink to={`${OURA_ROOT}/questions`} className="mt-3">
                See how AI names you
            </ArrowLink>
        </FoldCard>
    );
}

/** A report still to come: when it arrives, the month it covers, and what lands in that month. */
function ComingDrawer({ month, onClose }: { month: MerchantScheduleMonth | null; onClose: () => void }) {
    if (!month) return null;
    const on = dayDate(month.reportDate);
    return (
        <Drawer open onClose={onClose}>
            <DrawerHead title={reportName(month)} status={on ? { label: `Comes ${on}` } : null} />
            <DrawerBlock label="The month it covers">{month.window}</DrawerBlock>
            {month.lands.length > 0 && (
                <DrawerBlock label="What lands that month">
                    <ul className="list-plain m-0 mt-1 p-0">
                        {month.lands.map((line) => (
                            <li key={line} className="flex min-h-10 items-center border-t border-gray-100 first:border-t-0">
                                {line}
                            </li>
                        ))}
                    </ul>
                </DrawerBlock>
            )}
        </Drawer>
    );
}
